import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Phone, Mail, MapPin, ExternalLink } from 'lucide-react';
import { siteConfig, legalDisclaimer } from '@/data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[hsl(215,25%,12%)] text-white">
      {/* Disclaimer Banner */}
      <div className="bg-[hsl(35,90%,50%)] text-[hsl(215,25%,15%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <p className="text-sm font-medium">
              <strong>{legalDisclaimer.title}:</strong> {legalDisclaimer.content}
            </p>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[hsl(213,80%,35%)] flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-white">
                  {siteConfig.name.replace('.com', '')}
                </span>
                <span className="text-xl font-bold text-[hsl(213,70%,60%)]">.com</span>
              </div>
            </Link>
            <p className="text-[hsl(210,20%,70%)] text-sm leading-relaxed mb-6">
              {siteConfig.description}
            </p>
            <div className="space-y-3">
              <a
                href={`tel:${siteConfig.phone}`}
                className="flex items-center text-sm text-[hsl(210,20%,70%)] hover:text-white transition-colors"
              >
                <Phone className="w-4 h-4 mr-3" />
                {siteConfig.phone}
              </a>
              <a
                href={`mailto:${siteConfig.email}`}
                className="flex items-center text-sm text-[hsl(210,20%,70%)] hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4 mr-3" />
                {siteConfig.email}
              </a>
              <p className="flex items-center text-sm text-[hsl(210,20%,70%)]">
                <MapPin className="w-4 h-4 mr-3" />
                {siteConfig.address}
              </p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-6">Navigation</h4>
            <ul className="space-y-3">
              {[
                { path: '/', label: 'Startseite' },
                { path: '/ueber-mich', label: 'Über mich' },
                { path: '/leistungen', label: 'Leistungen' },
                { path: '/partner', label: 'Partner' },
                { path: '/kontakt', label: 'Kontakt' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-[hsl(210,20%,70%)] hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-6">Leistungen</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/leistungen" className="text-sm text-[hsl(210,20%,70%)] hover:text-white transition-colors">
                  Erstberatung
                </Link>
              </li>
              <li>
                <Link to="/leistungen" className="text-sm text-[hsl(210,20%,70%)] hover:text-white transition-colors">
                  Anwaltsvermittlung
                </Link>
              </li>
              <li>
                <Link to="/leistungen" className="text-sm text-[hsl(210,20%,70%)] hover:text-white transition-colors">
                  Behördenbegleitung
                </Link>
              </li>
              <li>
                <Link to="/ressourcen" className="text-sm text-[hsl(210,20%,70%)] hover:text-white transition-colors">
                  Ressourcen & Links
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-white font-semibold mb-6">Rechtliches</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/impressum" className="text-sm text-[hsl(210,20%,70%)] hover:text-white transition-colors">
                  Impressum
                </Link>
              </li>
              <li>
                <Link to="/datenschutz" className="text-sm text-[hsl(210,20%,70%)] hover:text-white transition-colors">
                  Datenschutz
                </Link>
              </li>
              <li>
                <a
                  href="https://www.rechtsanwaelte.at"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-sm text-[hsl(210,20%,70%)] hover:text-white transition-colors"
                >
                  Anwaltssuche
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-[hsl(215,20%,20%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-[hsl(210,20%,60%)]">
              © {currentYear} {siteConfig.name}. Alle Rechte vorbehalten.
            </p>
            <p className="text-xs text-[hsl(210,20%,50%)]">
              Diese Website bietet keine Rechtsberatung.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
